import { collection, query, where, getDocs } from 'firebase/firestore';
import { getSitesByOrganization } from '../services/siteService';

/**
 * Generate CSV content with headers and metadata
 * @param {Array} headers - Column headers
 * @param {Array} rows - Data rows
 * @param {Object} metadata - Additional metadata for the CSV
 * @returns {string} CSV content
 */
const generateCSVContent = (headers, rows, metadata = {}) => {
  const csvRows = [];
  
  // Add metadata header
  csvRows.push(`Export generated via CrisisCompass C3 | v1.0`);
  csvRows.push(`Export time: ${new Date().toLocaleString()}`);
  if (metadata.incidentTitle) {
    csvRows.push(`Incident: ${metadata.incidentTitle}`);
  }
  if (metadata.exportType) {
    csvRows.push(`Export type: ${metadata.exportType}`);
  }
  csvRows.push(''); // Empty line
  
  // Add headers
  csvRows.push(headers.join(','));
  
  // Add data rows
  rows.forEach(row => {
    const csvRow = row.map(field => {
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      const fieldStr = String(field || '');
      if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
        return `"${fieldStr.replace(/"/g, '""')}"`;
      }
      return fieldStr;
    }).join(',');
    csvRows.push(csvRow);
  });
  
  return csvRows.join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - The CSV content
 * @param {string} filename - The filename
 */
const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate filename for exports
 * @param {string} incidentTitle - The incident title
 * @param {string} exportType - 'SiteStatus' or 'StaffStatus'
 * @returns {string} Formatted filename
 */
const generateFilename = (incidentTitle, exportType) => {
  // Sanitize incident title
  const sanitizedTitle = incidentTitle.replace(/[^a-zA-Z0-9]/g, '_');
  
  // Generate timestamp
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
  
  return `${sanitizedTitle}_Export${exportType}_${dateStr}_${timeStr}.csv`;
};

/**
 * Export site status to CSV for a specific incident
 * @param {string} orgId - Organization ID
 * @param {string} incidentTitle - Incident title for filename
 * @param {Array} selectedRegions - Array of selected geography filters
 * @returns {Promise<string>} Filename of exported file
 */
export const exportIncidentSiteStatus = async (orgId, incidentTitle, selectedRegions = []) => {
  try {
    // Get all sites for the organization
    const allSites = await getSitesByOrganization(orgId);
    
    // Filter sites by selected regions if specified
    let filteredSites = allSites;
    if (selectedRegions.length > 0) {
      filteredSites = allSites.filter(site => {
        // Check if site matches any selected region
        return selectedRegions.some(region => {
          if (region.type === 'country') {
            return site.country === region.value;
          } else if (region.type === 'state') {
            return site.country === region.country && site.state === region.value;
          }
          return false;
        });
      });
    }
    
    // CSV Headers
    const headers = [
      'Site Name',
      'Site Type',
      'Location',
      'Address',
      'State/Province',
      'Country',
      'Status',
      'Last Updated',
      'Site Manager',
      'Manager Contact',
      'Latitude',
      'Longitude'
    ];
    
    // Convert sites to CSV rows
    const csvRows = filteredSites.map(site => [
      site.site_name || '',
      site.site_type || '',
      `${site.suburb || ''}, ${site.state || ''}`.replace(/^,\s*/, '').replace(/,\s*$/, ''),
      `${site.address_line_1 || ''} ${site.address_line_2 || ''}`.trim(),
      site.state || '',
      site.country || '',
      site.status || 'unknown',
      site.updated_at ? new Date(site.updated_at.seconds * 1000).toLocaleString() : '',
      site.site_manager_name || '',
      site.site_manager_mobile || site.site_manager_email || '',
      site.latitude || '',
      site.longitude || ''
    ]);
    
    // Generate CSV content
    const csvContent = generateCSVContent(headers, csvRows, {
      incidentTitle,
      exportType: 'Site Status'
    });
    
    // Generate filename and download
    const filename = generateFilename(incidentTitle, 'SiteStatus');
    downloadCSV(csvContent, filename);
    
    console.log(`✅ Exported ${filteredSites.length} sites to ${filename}`);
    return filename;
    
  } catch (error) {
    console.error('Error exporting site status:', error);
    throw error;
  }
};

/**
 * Export staff status to CSV for a specific incident
 * @param {Object} db - Firestore database instance
 * @param {string} incidentId - Incident ID
 * @param {string} orgId - Organization ID
 * @param {string} incidentTitle - Incident title for filename
 * @returns {Promise<string>} Filename of exported file
 */
export const exportIncidentStaffStatus = async (db, incidentId, orgId, incidentTitle) => {
  try {
    // Query staff status for this incident
    const staffRef = collection(db, 'c3_staff_status');
    const q = query(
      staffRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', orgId)
    );
    
    const snapshot = await getDocs(q);
    const staffList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // CSV Headers
    const headers = [
      'Staff Name',
      'Contact Number',
      'Assigned Location',
      'Current Status',
      'Last Update Method',
      'Last Updated',
      'Notes',
      'Emergency Contact',
      'Recorded By'
    ];
    
    // Convert staff to CSV rows
    const csvRows = staffList.map(staff => [
      staff.fullName || '',
      [staff.email, staff.phone].filter(Boolean).join(' | ') || staff.contactInfo || staff.phoneNumber || '',
      staff.location || '',
      staff.status || 'unaccounted',
      staff.createdVia === 'sms_reply' ? 'SMS' : 
        staff.manualEntry ? 'Manual' : 'System',
      staff.lastUpdated ? new Date(staff.lastUpdated.seconds * 1000).toLocaleString() : '',
      staff.notes || '',
      staff.emergencyContact || '',
      staff.createdByName || staff.statusChangedByName || ''
    ]);
    
    // Generate CSV content
    const csvContent = generateCSVContent(headers, csvRows, {
      incidentTitle,
      exportType: 'Staff Status'
    });
    
    // Generate filename and download
    const filename = generateFilename(incidentTitle, 'StaffStatus');
    downloadCSV(csvContent, filename);
    
    console.log(`✅ Exported ${staffList.length} staff members to ${filename}`);
    return filename;
    
  } catch (error) {
    console.error('Error exporting staff status:', error);
    throw error;
  }
};

/**
 * Get available regions for geography selection
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} Object with countries and states structure
 */
export const getAvailableRegions = async (orgId) => {
  try {
    const sites = await getSitesByOrganization(orgId);
    
    // Build regions structure
    const regions = {};
    
    sites.forEach(site => {
      const country = site.country || 'Unknown';
      const state = site.state || 'Unknown';
      
      if (!regions[country]) {
        regions[country] = new Set();
      }
      regions[country].add(state);
    });
    
    // Convert sets to arrays for easier use
    Object.keys(regions).forEach(country => {
      regions[country] = Array.from(regions[country]).sort();
    });
    
    return regions;
    
  } catch (error) {
    console.error('Error getting available regions:', error);
    throw error;
  }
}; 